import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Button, Image } from 'components/ui';
import {
    buttons, colors, forms, mediaQueries, radii, space
} from 'style/config';
import localeUtils from 'utils/LanguageLocale';
import GooglePayEN from 'utils/locales/GooglePay_en_US';
import GooglePayFR from 'utils/locales/GooglePay_fr_CA';

/**
 * Google Pay pill button (Release 4 / Checkout).
 *
 * Renders a UFE-styled pill that opens the in-page Google Pay selection state
 * (L2 view). The pill deliberately does NOT use Google's SDK `createButton`
 * helper: on iOS Safari / Chrome the SDK-painted button intercepts the tap with
 * its native handler and routes the customer to pay.google.com, bypassing the
 * `onClick` we register. Since the checkout PaymentsClient is not configured
 * for a full paysheet at this point (no PaymentDataRequest yet), the pay.google
 * page renders "Something went wrong" (PAY-7618). Mirrors the Venmo/Paze pill
 * approach, which is why those alternate payment methods do not exhibit the
 * same failure.
 *
 * Caller contract: parent already gated on the locale killswitch, the
 * `isGooglePayEnabledCheckout` flag, and a successful `initPaymentsClient()`
 * probe (see PAY-6993 for the future checkout-scoped eligibility helper).
 */
const GooglePayPill = ({ transactionAmount, isBopis, handleGooglePayClick = () => {} }) => {
    const getText = localeUtils.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);
    const label = getText('payWithGooglePay');

    return (
        <Button
            variant='primary'
            onClick={handleGooglePayClick}
            data-at={Sephora.debug.dataAt('checkout_googlepay_btn')}
            aria-label={label}
            css={styles}
            width={['100%', '151px', '100%']}
            data-transaction-amount={transactionAmount}
            data-is-bopis={isBopis ? 'true' : 'false'}
        >
            <Image
                src='/img/ufe/payments/gpayLogo.svg'
                alt=''
                aria-hidden='true'
                height='20px'
            />
        </Button>
    );
};

const styles = {
    backgroundColor: colors.black,
    borderRadius: radii.full,
    border: `${forms.BORDER_WIDTH}px solid ${colors.lightGray}`,
    height: buttons.HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${space[2]}px ${space[5]}px`,
    gap: space[3],
    flexShrink: 0,
    [mediaQueries.xsMax]: {
        padding: `${space[2]}px ${space[4]}px`
    },
    '&:hover': {
        backgroundColor: colors.white
    }
};

export default wrapFunctionalComponent(GooglePayPill, 'GooglePayPill');
